import { router } from '@forge/bridge'
import {
  ArrowTopRightOnSquareIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline'
import { useLocation, useNavigate } from 'react-router'
import { useSnapshot } from 'valtio'
import { logError } from '@/components/Error/Error'
import { state } from '../../lib/state'
import { CommonRadarSelector } from '../common/CommonRadarSelector'
import styles from './AdminNavigation.module.css'

const NavButton = ({ children }: { children: React.ReactNode }) => {
  // Show Featurebase Messenger on click
  const handleClick = () => {
    if (
      typeof window !== 'undefined' &&
      typeof window.Featurebase === 'function'
    ) {
      window.Featurebase('show', 'messages')
    }
  }

  return (
    <button
      type="button"
      className={`px-4 h-14 flex items-center cursor-pointer custom-chat-button ${styles.navLinkContainer}`}
      data-testid="nav-help-button"
      aria-label={`Navigate to ${children} section`}
      onClick={handleClick}
    >
      <span className={styles.navLink}>{children}</span>
    </button>
  )
}

export function AdminNavigation() {
  const { context, availableRadars, currentRadarId, isAdmin } =
    useSnapshot(state)
  const navigate = useNavigate()
  const location = useLocation()
  const currentPath = location.pathname

  const radarLink = context?.localId
    ? context.localId === 'local-dev'
      ? `/radar/${currentRadarId}`
      : `${context.siteUrl}/compass/app/${encodeURIComponent(context.localId.replace('techradarAdmin', 'techradarGlobal'))}`
    : undefined

  const isActive = (page: string) => currentPath === `/admin${page}`
  // Only show radar-specific tabs if there are radars and a valid currentRadarId
  const hasRadars = availableRadars && availableRadars?.length > 0

  // Add navigation handlers for admin routes
  const goToItems = (e: React.MouseEvent) => {
    e.preventDefault()
    try {
      navigate('/admin/items')
    } catch (error) {
      logError('AdminNavigation', 'Error navigating to items:', error)
    }
  }

  const goToBilling = (e: React.MouseEvent) => {
    e.preventDefault()
    try {
      navigate('/admin/billing')
    } catch (error) {
      logError('AdminNavigation', 'Error navigating to billing:', error)
    }
  }

  const goToSettings = (e: React.MouseEvent) => {
    e.preventDefault()
    try {
      navigate('/admin/settings')
    } catch (error) {
      logError('AdminNavigation', 'Error navigating to settings:', error)
    }
  }

  const hasRadar = hasRadars && !Number.isNaN(Number(currentRadarId))
  return (
    <nav className="admin-nav outline-none">
      <div className="px-4 flex items-center justify-between">
        <div className="flex items-center gap-2" data-testid="nav-left-section">
          {currentPath?.startsWith('/admin/edit-item') ? (
            <button
              type="button"
              className={
                'px-4 py-2 text-[var(--ds-link)] border border-[var(--ds-border)] hover:bg-[var(--ds-surface-hover)] transition rounded'
              }
              onClick={() => navigate('/admin/radar-list')}
              tabIndex={0}
              style={{
                cursor: 'pointer',
                background: 'var(--ds-surface)'
              }}
            >
              Manage Radars
            </button>
          ) : (
            <div style={{ marginRight: '16px' }}>
              <CommonRadarSelector
                onSelectRadar={(radarId: number) => {
                  // Admin context: Stay on current page but with new radar selected
                  state.currentRadarId = radarId
                }}
                onManageRadars={() => navigate('/admin/radar-list')}
                showAdminOptions={isAdmin}
                buttonLabel={
                  availableRadars?.find(
                    r => Number(r.id) === Number(currentRadarId)
                  )?.name || 'Tech Radar'
                }
              />
            </div>
          )}

          {/* Radar-specific items - only show when a radar is selected AND radars exist */}
          {hasRadar && (
            <>
              <div
                className={`admin-nav-tab ${
                  isActive('/items') ||
                  isActive('/edit-item') ||
                  isActive('/new-item') ||
                  currentPath.startsWith('/admin/edit-item')
                    ? 'admin-nav-tab-active'
                    : ''
                }`}
              >
                <a
                  className={`px-2 h-14 flex items-center cursor-pointer outline-none ${styles.navLinkContainer}`}
                  href="/admin/items"
                  onClick={goToItems}
                  data-testid="nav-items-link"
                  aria-current={
                    isActive('/items') ||
                    isActive('/edit-item') ||
                    isActive('/new-item') ||
                    currentPath.startsWith('/admin/edit-item')
                      ? 'page'
                      : undefined
                  }
                  role="tab"
                  aria-selected={
                    isActive('/items') ||
                    isActive('/edit-item') ||
                    isActive('/new-item') ||
                    currentPath.startsWith('/admin/edit-item')
                  }
                >
                  <span className={`${styles.navLink}`}>Radar Items</span>
                </a>
              </div>

              {(isActive('/edit-item') ||
                currentPath.startsWith('/admin/edit-item')) && (
                <>
                  <ChevronRightIcon
                    className="w-3 h-3 text-[var(--ds-text-subtlest)]"
                    aria-hidden="true"
                    data-testid="nav-chevron-icon"
                  />
                  <span
                    className="text-sm text-[var(--ds-text-subtlest)] ml-1"
                    data-testid="nav-edit-item"
                  >
                    Edit Item
                  </span>
                </>
              )}
              {isActive('/new-item') && (
                <>
                  <ChevronRightIcon
                    className="w-3 h-3 text-[var(--ds-text-subtlest)]"
                    aria-hidden="true"
                    data-testid="nav-chevron-icon"
                  />
                  <span
                    className="text-sm text-[var(--ds-text-subtlest)] ml-1"
                    data-testid="nav-new-item"
                  >
                    New Item
                  </span>
                </>
              )}
            </>
          )}
        </div>
        <div
          className="flex items-center gap-4"
          data-testid="nav-right-section"
        >
          <div className="border-b-2 border-transparent">
            <button
              type="button"
              className={`px-4 h-14 flex items-center cursor-pointer outline-none ${styles.navLinkContainer}`}
              data-testid="nav-radar-link"
              aria-label="View Radar"
              onClick={e => {
                e.preventDefault()
                if (context?.localId === 'local-dev') {
                  // For development, use React Router navigation (no reload)
                  const radarIdToUse =
                    currentRadarId || availableRadars?.[0]?.id
                  if (radarIdToUse) {
                    navigate(`/radar/${radarIdToUse}`)
                  }
                } else {
                  // For production, open in new tab
                  if (radarLink) {
                    router.open(radarLink)
                  }
                }
              }}
            >
              <span className={`${styles.navLink} flex items-center`}>
                Radar
                <ArrowTopRightOnSquareIcon
                  className="w-3.5 h-3.5 ml-1"
                  aria-hidden="true"
                />
              </span>
            </button>
          </div>

          {/* Settings Tab - conditional on currentRadarId AND radars exist */}
          {hasRadar && (
            <div
              className={`admin-nav-tab ${
                isActive('/settings') ? 'admin-nav-tab-active' : ''
              }`}
            >
              <a
                className={`px-4 h-14 flex items-center cursor-pointer outline-none ${styles.navLinkContainer}`}
                href="/admin/settings"
                onClick={goToSettings}
                data-testid="nav-settings-link"
                aria-current={isActive('/settings') ? 'page' : undefined}
                role="tab"
                aria-selected={isActive('/settings')}
              >
                <span
                  className={`${styles.navLink} ${
                    isActive('/settings')
                      ? 'text-[var(--ds-text-link)] font-medium'
                      : ''
                  }`}
                >
                  Settings
                </span>
              </a>
            </div>
          )}

          {/* Global items - always show regardless of radar selection */}
          <div
            className={`admin-nav-tab ${
              isActive('/billing') ? 'admin-nav-tab-active' : ''
            }`}
          >
            <a
              className={`px-4 h-14 flex items-center cursor-pointer outline-none ${styles.navLinkContainer}`}
              href="/admin/billing"
              onClick={goToBilling}
              data-testid="nav-billing-link"
              aria-current={isActive('/billing') ? 'page' : undefined}
              role="tab"
              aria-selected={isActive('/billing')}
            >
              <span
                className={`${styles.navLink} ${
                  isActive('/billing')
                    ? 'text-[var(--ds-text-link)] font-medium'
                    : ''
                }`}
              >
                Billing
              </span>
            </a>
          </div>
          <div className="border-b-2 border-transparent">
            <NavButton>Help</NavButton>
          </div>
        </div>
      </div>
    </nav>
  )
}
